import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import { pageType, permissionActions, permissionKeys } from 'src/utils/constants';
import { UserType } from 'src/utils/proptypes';

type RoleLike = { type?: string; permissions?: unknown[] } | null | undefined;

const toKeys = (role: RoleLike): string[] => (role?.permissions ?? []).filter((permission): permission is string => typeof permission === 'string');

/**
 * NPT-2144 — the organization the user is acting in: the org context AuthContext set the cookie
 * for, else the first membership (the same fallback AuthContext uses when it sets the cookie).
 */
export const resolveCurrentOrganization = (user: UserType | null, currentOrganizationId?: string | null) => {
   const organizations = user?.organizations ?? [];
   if (!organizations.length) return undefined;
   // Membership items carry the org id as both `id` and `organizationId` (npt-api OrganizationDto).
   const matches = (org: (typeof organizations)[number]) => org.id === currentOrganizationId || (org as { organizationId?: string }).organizationId === currentOrganizationId;
   return organizations.find(matches) ?? (currentOrganizationId ? undefined : organizations[0]);
};

/**
 * Permission keys the user's role in the current org carries. PLATFORM_ADMIN is never honoured
 * from an org role: platform access comes from site roles only, as the API guard enforces.
 */
export const getCurrentOrgRolePermissions = (user: UserType | null, currentOrganizationId?: string | null): string[] => {
   const organization = resolveCurrentOrganization(user, currentOrganizationId) as { role?: RoleLike } | undefined;
   return toKeys(organization?.role).filter(key => key !== permissionKeys.PLATFORM_ADMIN);
};

/** Site-wide permission keys: Organization-typed roles in `roles` grant nothing site wide (NPT-2144). */
export const getSitePermissions = (user: UserType | null): string[] =>
   (user?.roles ?? []).filter(role => role?.type !== pageType.ORGANIZATION).flatMap(role => toKeys(role));

export const defineAbilityFor = (user: UserType | null, currentOrganizationId?: string | null) => {
   const { can, build } = new AbilityBuilder(createMongoAbility);

   // For unauthenticated users
   if (!user || !user.roles) {
      return build();
   }

   const grant = (permission: string) => {
      if (permission === permissionKeys.PLATFORM_ADMIN) {
         can(permissionActions.MANAGE, 'all');
         return;
      }
      const [action, subject] = permission.split('_');
      if (action && subject) can(action.toLowerCase(), subject.toLowerCase());
   };

   getSitePermissions(user).forEach(grant);
   getCurrentOrgRolePermissions(user, currentOrganizationId).forEach(grant);

   return build();
};
